import { c as _c } from "react/compiler-runtime";
// «Новая загрузка»: куда грузим (магазин или новый магазин из заявки / по
// контактам), подсказка и модель, настройки разбора. После создания —
// сразу в мастер, на шаг материалов.
import { Check, Loader2, Store } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type { CatalogImportJobCreate, CatalogImportOptions } from "@osmix/contracts";
import { api } from "@/api";
import { useApplicationBoard } from "@/api/hooks/use-applications";
import { useCatalogImportModels, useCreateImportJob } from "@/api/hooks/use-catalog-import";
import { useSellers } from "@/api/hooks/use-sellers";
import { useStorefronts } from "@/api/hooks/use-storefronts";
import { Segmented } from "@/components/segmented";
import { Select } from "@/components/ui/select";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";
import { cn } from "@/lib/utils";
import { CategorySelect } from "./category-select";
import { errorText, formatDate } from "./shared";
export type NewImportPreset = {
  kind: "existing_shop";
  shopId: string;
} | {
  kind: "new_shop";
  applicationId?: string;
};
type ShopOption = {
  id: string;
  name: string;
  sub: string;
};
const LIST_LIMIT = 8;
const priceLabel = (n: number) => `$${n.toLocaleString("ru-RU", {
  maximumFractionDigits: 2
})}`;
const textareaClass = "min-h-20 w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 md:text-sm";
export function NewImportDialog(t0) {
  const $ = _c(10);
  const {
    open,
    preset,
    onOpenChange
  } = t0;
  let t1;
  if ($[0] !== onOpenChange || $[1] !== open || $[2] !== preset) {
    t1 = open && <NewImportForm preset={preset ?? null} onClose={() => onOpenChange(false)} />;
    $[0] = onOpenChange;
    $[1] = open;
    $[2] = preset;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <DialogContent className="flex max-h-[90dvh] flex-col gap-0 p-0 sm:max-w-2xl">{t1}</DialogContent>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== onOpenChange || $[7] !== open || $[8] !== t2) {
    t3 = <Dialog open={open} onOpenChange={onOpenChange}>{t2}</Dialog>;
    $[6] = onOpenChange;
    $[7] = open;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function NewImportForm(t0) {
  const $ = _c(141);
  const {
    preset,
    onClose
  } = t0;
  const navigate = useNavigate();
  const create = useCreateImportJob();
  const modelsQuery = useCatalogImportModels();
  const sellers = useSellers().data ?? [];
  const storefronts = useStorefronts().data ?? [];
  const board = useApplicationBoard().data;
  const [kind, setKind] = useState(preset?.kind ?? "existing_shop");
  const [shopId, setShopId] = useState(preset?.kind === "existing_shop" ? preset.shopId : null);
  const [shopQuery, setShopQuery] = useState("");
  const [source, setSource] = useState(preset?.kind === "new_shop" && !preset.applicationId ? "contacts" : "application");
  const [applicationId, setApplicationId] = useState(preset?.kind === "new_shop" ? preset.applicationId ?? null : null);
  const [appQuery, setAppQuery] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: "",
      phone: "",
      email: ""
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [contact, setContact] = useState(t1);
  const [sendInvite, setSendInvite] = useState(false);
  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [model, setModel] = useState("");
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      publishMode: "draft",
      rewriteDescriptions: true,
      matchPhotosByVision: true,
      fillAttributes: true
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [options, setOptions] = useState(t2);
  const [error, setError] = useState(null);
  const [account, setAccount] = useState(null);
  const owned = new Set(sellers.map(_temp));
  const shops = [...sellers.map(_temp2), ...storefronts.filter(sf => !owned.has(sf.id)).map(_temp3)];
  const sq = shopQuery.trim().toLowerCase();
  const shopMatches = (sq ? shops.filter(s_1 => `${s_1.name} ${s_1.sub}`.toLowerCase().includes(sq)) : shops).slice(0, LIST_LIMIT);
  const selectedShop = shops.find(s_2 => s_2.id === shopId) ?? (shopId ? {
    id: shopId,
    name: "\u041C\u0430\u0433\u0430\u0437\u0438\u043D",
    sub: shopId
  } : null);
  const applications = board?.applications ?? [];
  const aq = appQuery.trim().toLowerCase();
  const appMatches = (aq ? applications.filter(a => [a.shopName, a.contactName, a.phone, a.email, a.city].filter(Boolean).join(" ").toLowerCase().includes(aq)) : applications).slice(0, LIST_LIMIT);
  const selectedApp = applications.find(a_0 => a_0.id === applicationId) ?? null;
  const appUserId = selectedApp?.userId;
  useEffect(() => {
    setAccount(null);
    if (!applicationId) {
      return;
    }
    const seller = appUserId ? sellers.find(s_3 => s_3.id === appUserId) : undefined;
    if (seller) {
      setAccount({
        shopId: seller.shopId ?? seller.id,
        shopName: seller.shopName
      });
      return;
    }
    let alive = true;
    api.applicationCrm.card(applicationId).then(card => {
      if (alive && card.account?.shopId) {
        setAccount({
          shopId: card.account.shopId,
          shopName: card.account.shopName
        });
      }
    }).catch(_temp4);
    return () => {
      alive = false;
    };
  }, [applicationId, appUserId, sellers.length]);
  let t3;
  if ($[2] !== modelsQuery.data?.models) {
    t3 = modelsQuery.data?.models ?? [];
    $[2] = modelsQuery.data?.models;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const models = t3;
  const modelId = model || modelsQuery.data?.default || "";
  let t4;
  if ($[4] !== modelId || $[5] !== models) {
    let t5;
    if ($[7] !== modelId) {
      t5 = m => m.id === modelId;
      $[7] = modelId;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t4 = models.find(t5);
    $[4] = modelId;
    $[5] = models;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const currentModel = t4;
  const vision = currentModel?.vision ?? true;
  const who = kind === "existing_shop" ? selectedShop?.name : source === "application" ? selectedApp?.shopName : contact.name.trim() || undefined;
  const placeholderTitle = `Каталог «${who ?? "\u2026"}» от ${formatDate(new Date().toISOString())}`;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (key, value) => setOptions(prev => ({
      ...prev,
      [key]: value
    }));
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const setOption = t5;
  let t6;
  if ($[10] !== applicationId || $[11] !== contact || $[12] !== instructions || $[13] !== kind || $[14] !== modelId || $[15] !== options || $[16] !== sendInvite || $[17] !== shopId || $[18] !== source || $[19] !== title || $[20] !== vision) {
    t6 = () => {
      let target;
      if (kind === "existing_shop") {
        if (!shopId) {
          return "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043C\u0430\u0433\u0430\u0437\u0438\u043D, \u0432 \u043A\u043E\u0442\u043E\u0440\u044B\u0439 \u0433\u0440\u0443\u0437\u0438\u043C \u0442\u043E\u0432\u0430\u0440\u044B.";
        }
        target = {
          kind: "existing_shop",
          shopId
        };
      } else {
        if (source === "application") {
          if (!applicationId) {
            return "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0437\u0430\u044F\u0432\u043A\u0443 \u2014 \u0438\u0437 \u043D\u0435\u0451 \u0432\u043E\u0437\u044C\u043C\u0451\u043C \u043A\u043E\u043D\u0442\u0430\u043A\u0442\u044B \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u0430.";
          }
          target = {
            kind: "new_shop",
            applicationId,
            sendInvite
          };
        } else {
          const c = {
            name: contact.name.trim(),
            phone: contact.phone.trim(),
            email: contact.email.trim()
          };
          if (!c.name && !c.phone && !c.email) {
            return "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u0445\u043E\u0442\u044F \u0431\u044B \u043E\u0434\u0438\u043D \u043A\u043E\u043D\u0442\u0430\u043A\u0442 \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u0430: \u0438\u043C\u044F, \u0442\u0435\u043B\u0435\u0444\u043E\u043D \u0438\u043B\u0438 \u043F\u043E\u0447\u0442\u0443.";
          }
          if (c.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)) {
            return "\u041F\u043E\u0447\u0442\u0430 \u0443\u043A\u0430\u0437\u0430\u043D\u0430 \u0441 \u043E\u0448\u0438\u0431\u043A\u043E\u0439.";
          }
          if (sendInvite && !c.phone && !c.email) {
            return "\u0414\u043B\u044F \u043F\u0440\u0438\u0433\u043B\u0430\u0448\u0435\u043D\u0438\u044F \u043D\u0443\u0436\u0435\u043D \u0442\u0435\u043B\u0435\u0444\u043E\u043D \u0438\u043B\u0438 \u043F\u043E\u0447\u0442\u0430.";
          }
          target = {
            kind: "new_shop",
            contact: {
              name: c.name || undefined,
              phone: c.phone || undefined,
              email: c.email || undefined
            },
            sendInvite
          };
        }
      }
      return {
        title: title.trim() || undefined,
        target,
        instructions: instructions.trim() || undefined,
        model: modelId || undefined,
        options: {
          ...options,
          matchPhotosByVision: vision && options.matchPhotosByVision,
          defaultBrand: options.defaultBrand?.trim() || undefined,
          defaultCategoryId: options.defaultCategoryId || undefined
        }
      };
    };
    $[10] = applicationId;
    $[11] = contact;
    $[12] = instructions;
    $[13] = kind;
    $[14] = modelId;
    $[15] = options;
    $[16] = sendInvite;
    $[17] = shopId;
    $[18] = source;
    $[19] = title;
    $[20] = vision;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const buildInput = t6;
  let t7;
  if ($[22] !== buildInput || $[23] !== create || $[24] !== navigate || $[25] !== onClose) {
    t7 = async () => {
      const input = buildInput();
      if (typeof input === "string") {
        setError(input);
        return;
      }
      setError(null);
      ;
      try {
        const job = await create.mutateAsync(input);
        onClose();
        navigate(`/catalog-import/${job.id}`);
      } catch (t8) {
        const e = t8;
        setError(errorText(e));
      }
    };
    $[22] = buildInput;
    $[23] = create;
    $[24] = navigate;
    $[25] = onClose;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const submit = t7;
  let t8;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DialogHeader className="border-b p-4 pr-12 sm:p-6 sm:pr-12"><DialogTitle>Новая загрузка каталога</DialogTitle><DialogDescription>Материалы добавите на следующем шаге: прайс, каталог PDF, фото, архив или текст из переписки.</DialogDescription></DialogHeader>;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const t9 = "min-h-0 flex-1 space-y-6 overflow-y-auto p-4 sm:p-6";
  const t10 = "space-y-3";
  let t11;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <h3 className="text-sm font-semibold">Куда грузим</h3>;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [{
      value: "existing_shop",
      label: "\u0412 \u0441\u0443\u0449\u0435\u0441\u0442\u0432\u0443\u044E\u0449\u0438\u0439"
    }, {
      value: "new_shop",
      label: "\u0412 \u043D\u043E\u0432\u044B\u0439 \u043C\u0430\u0433\u0430\u0437\u0438\u043D"
    }];
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== kind) {
    t13 = <Segmented value={kind} onChange={setKind} options={t12} />;
    $[30] = kind;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const t14 = kind === "existing_shop" ? <div className="space-y-2"><Input value={shopQuery} onChange={e_0 => setShopQuery(e_0.target.value)} placeholder={"\u041F\u043E\u0438\u0441\u043A \u043F\u043E \u043C\u0430\u0433\u0430\u0437\u0438\u043D\u0443 \u0438\u043B\u0438 \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u0443"} aria-label={"\u041F\u043E\u0438\u0441\u043A \u043C\u0430\u0433\u0430\u0437\u0438\u043D\u0430"} className="h-9" /><PickList empty={shops.length === 0 ? "\u0417\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u043C \u043C\u0430\u0433\u0430\u0437\u0438\u043D\u044B\u2026" : "\u041D\u0438\u0447\u0435\u0433\u043E \u043D\u0435 \u043D\u0430\u0448\u043B\u043E\u0441\u044C"} items={shopMatches.map(_temp5)} selected={shopId} onSelect={setShopId} />{selectedShop && !shopMatches.some(s_5 => s_5.id === selectedShop.id) && <p className="text-sm">Выбран: <span className="font-medium">{selectedShop.name}</span></p>}</div> : <div className="space-y-3"><Segmented value={source} onChange={setSource} options={[{
      value: "application",
      label: "\u0418\u0437 \u0437\u0430\u044F\u0432\u043A\u0438"
    }, {
      value: "contacts",
      label: "\u041F\u043E \u043A\u043E\u043D\u0442\u0430\u043A\u0442\u0430\u043C"
    }]} />{source === "application" ? <div className="space-y-2"><Input value={appQuery} onChange={e_1 => setAppQuery(e_1.target.value)} placeholder={"\u041C\u0430\u0433\u0430\u0437\u0438\u043D, \u043A\u043E\u043D\u0442\u0430\u043A\u0442, \u0442\u0435\u043B\u0435\u0444\u043E\u043D \u0438\u043B\u0438 \u043F\u043E\u0447\u0442\u0430 \u0438\u0437 \u0437\u0430\u044F\u0432\u043A\u0438"} aria-label={"\u041F\u043E\u0438\u0441\u043A \u0437\u0430\u044F\u0432\u043A\u0438"} className="h-9" /><PickList empty={board ? "\u0417\u0430\u044F\u0432\u043E\u043A \u043D\u0435 \u043D\u0430\u0448\u043B\u043E\u0441\u044C" : "\u0417\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u043C \u0437\u0430\u044F\u0432\u043A\u0438\u2026"} items={appMatches.map(_temp6)} selected={applicationId} onSelect={setApplicationId} />{applicationId && !selectedApp && board && <p className="text-sm text-muted-foreground">Заявка {applicationId} не найдена на доске.</p>}{account && <div className="flex flex-col gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm sm:flex-row sm:items-center sm:justify-between"><span>У заявки уже есть кабинет продавца — магазин «{account.shopName}». Новый магазин заводить не нужно.</span><Button size="sm" variant="outline" className="shrink-0" onClick={() => {
          setKind("existing_shop");
          setShopId(account.shopId);
        }}><Store className="size-4" />Грузить в него</Button></div>}</div> : <div className="grid gap-3 sm:grid-cols-3"><Field label={"\u0418\u043C\u044F"} htmlFor="ci-contact-name"><Input id="ci-contact-name" value={contact.name} onChange={e_2 => setContact(c_0 => ({
          ...c_0,
          name: e_2.target.value
        }))} autoComplete="off" /></Field><Field label={"\u0422\u0435\u043B\u0435\u0444\u043E\u043D"} htmlFor="ci-contact-phone"><Input id="ci-contact-phone" type="tel" value={contact.phone} onChange={e_3 => setContact(c_1 => ({
          ...c_1,
          phone: e_3.target.value
        }))} placeholder="+7 900 000-00-00" autoComplete="off" /></Field><Field label={"\u041F\u043E\u0447\u0442\u0430"} htmlFor="ci-contact-email"><Input id="ci-contact-email" type="email" value={contact.email} onChange={e_4 => setContact(c_2 => ({
          ...c_2,
          email: e_4.target.value
        }))} autoComplete="off" /></Field></div>}<label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-0.5 size-4" checked={sendInvite} onChange={e_5 => setSendInvite(e_5.target.checked)} /><span>Отправить продавцу приглашение<span className="block text-xs text-muted-foreground">SMS и письмо со ссылкой в кабинет уйдут сразу после загрузки товаров.</span></span></label></div>;
  let t15;
  if ($[32] !== t11 || $[33] !== t13 || $[34] !== t14) {
    t15 = <section className={t10}>{t11}{t13}{t14}</section>;
    $[32] = t11;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== setTitle) {
    t16 = e_6 => setTitle(e_6.target.value);
    $[36] = setTitle;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== placeholderTitle || $[39] !== t16 || $[40] !== title) {
    t17 = <Field label={"\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0437\u0430\u0434\u0430\u043D\u0438\u044F"} htmlFor="ci-title"><Input id="ci-title" value={title} onChange={t16} placeholder={placeholderTitle} /></Field>;
    $[38] = placeholderTitle;
    $[39] = t16;
    $[40] = title;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== setInstructions) {
    t18 = e_7 => setInstructions(e_7.target.value);
    $[42] = setInstructions;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== instructions || $[45] !== t18) {
    t19 = <Field label={"\u041F\u043E\u0434\u0441\u043A\u0430\u0437\u043A\u0430 \u043C\u043E\u0434\u0435\u043B\u0438"} htmlFor="ci-instructions" hint={"\u0427\u0442\u043E \u043C\u043E\u0434\u0435\u043B\u044C \u043D\u0435 \u043F\u043E\u0439\u043C\u0451\u0442 \u0438\u0437 \u0444\u0430\u0439\u043B\u043E\u0432 \u0441\u0430\u043C\u0430: \u043D\u0430\u0446\u0435\u043D\u043A\u0430, \u0441\u0435\u0437\u043E\u043D, \u043A\u0430\u043A\u0438\u0435 \u043F\u043E\u0437\u0438\u0446\u0438\u0438 \u043F\u0440\u043E\u043F\u0443\u0441\u0442\u0438\u0442\u044C."}><textarea id="ci-instructions" value={instructions} onChange={t18} placeholder={"\u0426\u0435\u043D\u044B \u0432 \u043F\u0440\u0430\u0439\u0441\u0435 \u043E\u043F\u0442\u043E\u0432\u044B\u0435, \u0440\u043E\u0437\u043D\u0438\u0446\u0430 \xD72. \u042D\u0442\u043E \u043B\u0435\u0442\u043D\u044F\u044F \u043A\u043E\u043B\u043B\u0435\u043A\u0446\u0438\u044F."} className={textareaClass} /></Field>;
    $[44] = instructions;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  const t20 = currentModel?.note;
  let t21;
  if ($[47] !== setModel) {
    t21 = e_8 => setModel(e_8.target.value);
    $[47] = setModel;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const t22 = !models.length;
  let t23;
  if ($[49] !== models.length) {
    t23 = !models.length && <option value="">Загружаем модели…</option>;
    $[49] = models.length;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== models) {
    t24 = models.map(_temp7);
    $[51] = models;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== modelId || $[54] !== t21 || $[55] !== t22 || $[56] !== t23 || $[57] !== t24) {
    t25 = <div className="grid"><Select id="ci-model" value={modelId} onChange={t21} disabled={t22}>{t23}{t24}</Select></div>;
    $[53] = modelId;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== t20 || $[60] !== t25) {
    t26 = <Field label={"\u041C\u043E\u0434\u0435\u043B\u044C"} htmlFor="ci-model" hint={t20}>{t25}</Field>;
    $[59] = t20;
    $[60] = t25;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== modelsQuery.data) {
    t27 = modelsQuery.data && !modelsQuery.data.enabled && <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">Ключ OpenRouter на площадке не настроен: без модели разберутся только файлы по нашему шаблону.</p>;
    $[62] = modelsQuery.data;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t17 || $[65] !== t19 || $[66] !== t26 || $[67] !== t27) {
    t28 = <section className="grid gap-4">{t17}{t19}{t26}{t27}</section>;
    $[64] = t17;
    $[65] = t19;
    $[66] = t26;
    $[67] = t27;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <h3 className="text-sm font-semibold">Настройки разбора</h3>;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = [["draft", "\u0427\u0435\u0440\u043D\u043E\u0432\u0438\u043A\u0430\u043C\u0438", "\u0412\u0438\u0434\u0438\u0442 \u0442\u043E\u043B\u044C\u043A\u043E \u043F\u0440\u043E\u0434\u0430\u0432\u0435\u0446; \u043D\u0430 \u043C\u043E\u0434\u0435\u0440\u0430\u0446\u0438\u044E \u043E\u0442\u043F\u0440\u0430\u0432\u0438\u0442 \u0441\u0430\u043C"], ["moderation", "\u0421\u0440\u0430\u0437\u0443 \u043D\u0430 \u043C\u043E\u0434\u0435\u0440\u0430\u0446\u0438\u044E", "\u041A\u0430\u0440\u0442\u043E\u0447\u043A\u0438 \u0432\u0441\u0442\u0430\u043D\u0443\u0442 \u0432 \u043E\u0447\u0435\u0440\u0435\u0434\u044C \u043C\u043E\u0434\u0435\u0440\u0430\u0442\u043E\u0440\u043E\u0432"]];
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== options.publishMode || $[72] !== setOption) {
    t31 = <div className="grid gap-2 sm:grid-cols-2">{(t30 as const).map(t32 => {
        const [value_0, label, hint] = t32;
        return <label key={value_0} className={cn("flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm", options.publishMode === value_0 && "border-primary bg-primary/5")}><input type="radio" name="ci-publish-mode" className="mt-0.5 size-4" checked={options.publishMode === value_0} onChange={() => setOption("publishMode", value_0)} /><span>{label}<span className="block text-xs text-muted-foreground">{hint}</span></span></label>;
      })}</div>;
    $[71] = options.publishMode;
    $[72] = setOption;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== setOption) {
    t32 = v => setOption("rewriteDescriptions", v);
    $[74] = setOption;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Hint>По фактам из материалов, без выдумок. Выключено — берём текст как есть.</Hint>;
    $[76] = t33;
  } else {
    t33 = $[76];
  }
  let t34;
  if ($[77] !== options.rewriteDescriptions || $[78] !== t32) {
    t34 = <Check2 checked={options.rewriteDescriptions} onChange={t32}>Переписывать описания{t33}</Check2>;
    $[77] = options.rewriteDescriptions;
    $[78] = t32;
    $[79] = t34;
  } else {
    t34 = $[79];
  }
  const t35 = vision && options.matchPhotosByVision;
  const t36 = !vision;
  let t37;
  if ($[80] !== setOption) {
    t37 = v_0 => setOption("matchPhotosByVision", v_0);
    $[80] = setOption;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  const t38 = vision ? "\u041C\u043E\u0434\u0435\u043B\u044C \u0441\u043C\u043E\u0442\u0440\u0438\u0442 \u043D\u0430 \u0444\u043E\u0442\u043E \u2014 \u0442\u043E\u0447\u043D\u0435\u0435, \u043D\u043E \u0434\u043E\u0440\u043E\u0436\u0435." : "\u0412\u044B\u0431\u0440\u0430\u043D\u043D\u0430\u044F \u043C\u043E\u0434\u0435\u043B\u044C \u043D\u0435 \u0432\u0438\u0434\u0438\u0442 \u0444\u043E\u0442\u043E.";
  let t39;
  if ($[82] !== t38) {
    t39 = <Hint>{t38}</Hint>;
    $[82] = t38;
    $[83] = t39;
  } else {
    t39 = $[83];
  }
  let t40;
  if ($[84] !== t35 || $[85] !== t36 || $[86] !== t37 || $[87] !== t39) {
    t40 = <Check2 checked={t35} disabled={t36} onChange={t37}>Сверять фото с товарами по картинке{t39}</Check2>;
    $[84] = t35;
    $[85] = t36;
    $[86] = t37;
    $[87] = t39;
    $[88] = t40;
  } else {
    t40 = $[88];
  }
  let t41;
  if ($[89] !== setOption) {
    t41 = v_1 => setOption("fillAttributes", v_1);
    $[89] = setOption;
    $[90] = t41;
  } else {
    t41 = $[90];
  }
  let t42;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <Hint>Состав, сезон, посадка — из материалов, по схеме категории.</Hint>;
    $[91] = t42;
  } else {
    t42 = $[91];
  }
  let t43;
  if ($[92] !== options.fillAttributes || $[93] !== t41) {
    t43 = <Check2 checked={options.fillAttributes} onChange={t41}>Заполнять характеристики{t42}</Check2>;
    $[92] = options.fillAttributes;
    $[93] = t41;
    $[94] = t43;
  } else {
    t43 = $[94];
  }
  let t44;
  if ($[95] !== t34 || $[96] !== t40 || $[97] !== t43) {
    t44 = <div className="space-y-2">{t34}{t40}{t43}</div>;
    $[95] = t34;
    $[96] = t40;
    $[97] = t43;
    $[98] = t44;
  } else {
    t44 = $[98];
  }
  const t45 = options.defaultCategoryId ?? null;
  let t46;
  if ($[99] !== setOption) {
    t46 = id => setOption("defaultCategoryId", id ?? undefined);
    $[99] = setOption;
    $[100] = t46;
  } else {
    t46 = $[100];
  }
  let t47;
  if ($[101] !== t45 || $[102] !== t46) {
    t47 = <Field label={"\u041A\u0430\u0442\u0435\u0433\u043E\u0440\u0438\u044F \u043F\u043E \u0443\u043C\u043E\u043B\u0447\u0430\u043D\u0438\u044E"} htmlFor="ci-default-category" hint={"\u0414\u043B\u044F \u043F\u043E\u0437\u0438\u0446\u0438\u0439, \u0433\u0434\u0435 \u043C\u043E\u0434\u0435\u043B\u044C \u043D\u0435 \u0443\u0432\u0435\u0440\u0435\u043D\u0430."}><div className="grid"><CategorySelect id="ci-default-category" value={t45} emptyLabel={"\u041D\u0435 \u0437\u0430\u0434\u0430\u043D\u0430"} onChange={t46} /></div></Field>;
    $[101] = t45;
    $[102] = t46;
    $[103] = t47;
  } else {
    t47 = $[103];
  }
  const t48 = options.defaultBrand ?? "";
  let t49;
  if ($[104] !== setOption) {
    t49 = e_9 => setOption("defaultBrand", e_9.target.value);
    $[104] = setOption;
    $[105] = t49;
  } else {
    t49 = $[105];
  }
  let t50;
  if ($[106] !== t48 || $[107] !== t49) {
    t50 = <Field label={"\u0411\u0440\u0435\u043D\u0434 \u043F\u043E \u0443\u043C\u043E\u043B\u0447\u0430\u043D\u0438\u044E"} htmlFor="ci-default-brand" hint={"\u0415\u0441\u043B\u0438 \u0432 \u043C\u0430\u0442\u0435\u0440\u0438\u0430\u043B\u0430\u0445 \u0431\u0440\u0435\u043D\u0434\u0430 \u043D\u0435\u0442."}><Input id="ci-default-brand" value={t48} onChange={t49} /></Field>;
    $[106] = t48;
    $[107] = t49;
    $[108] = t50;
  } else {
    t50 = $[108];
  }
  let t51;
  if ($[109] !== t47 || $[110] !== t50) {
    t51 = <div className="grid gap-3 sm:grid-cols-2">{t47}{t50}</div>;
    $[109] = t47;
    $[110] = t50;
    $[111] = t51;
  } else {
    t51 = $[111];
  }
  let t52;
  if ($[112] !== t31 || $[113] !== t44 || $[114] !== t51) {
    t52 = <section className="space-y-3">{t29}{t31}{t44}{t51}</section>;
    $[112] = t31;
    $[113] = t44;
    $[114] = t51;
    $[115] = t52;
  } else {
    t52 = $[115];
  }
  let t53;
  if ($[116] !== t15 || $[117] !== t28 || $[118] !== t52) {
    t53 = <div className={t9}>{t15}{t28}{t52}</div>;
    $[116] = t15;
    $[117] = t28;
    $[118] = t52;
    $[119] = t53;
  } else {
    t53 = $[119];
  }
  let t54;
  if ($[120] !== error) {
    t54 = error && <p role="alert" className="text-sm text-destructive sm:mr-auto">{error}</p>;
    $[120] = error;
    $[121] = t54;
  } else {
    t54 = $[121];
  }
  let t55;
  if ($[122] !== create.isPending || $[123] !== onClose) {
    t55 = <Button variant="outline" onClick={onClose} disabled={create.isPending}>Отмена</Button>;
    $[122] = create.isPending;
    $[123] = onClose;
    $[124] = t55;
  } else {
    t55 = $[124];
  }
  let t56;
  if ($[125] !== submit) {
    t56 = () => void submit();
    $[125] = submit;
    $[126] = t56;
  } else {
    t56 = $[126];
  }
  let t57;
  if ($[127] !== create.isPending) {
    t57 = create.isPending && <Loader2 className="size-4 animate-spin" />;
    $[127] = create.isPending;
    $[128] = t57;
  } else {
    t57 = $[128];
  }
  let t58;
  if ($[129] !== create.isPending || $[130] !== t56 || $[131] !== t57) {
    t58 = <Button onClick={t56} disabled={create.isPending}>{t57}Создать и добавить материалы</Button>;
    $[129] = create.isPending;
    $[130] = t56;
    $[131] = t57;
    $[132] = t58;
  } else {
    t58 = $[132];
  }
  let t59;
  if ($[133] !== t54 || $[134] !== t55 || $[135] !== t58) {
    t59 = <div className="flex flex-col gap-2 border-t p-4 sm:flex-row sm:items-center sm:justify-end">{t54}{t55}{t58}</div>;
    $[133] = t54;
    $[134] = t55;
    $[135] = t58;
    $[136] = t59;
  } else {
    t59 = $[136];
  }
  let t60;
  if ($[137] !== t53 || $[138] !== t59 || $[139] !== t8) {
    t60 = <>{t8}{t53}{t59}</>;
    $[137] = t53;
    $[138] = t59;
    $[139] = t8;
    $[140] = t60;
  } else {
    t60 = $[140];
  }
  return t60;
}
function _temp7(m_0) {
  return <option key={m_0.id} value={m_0.id}>{m_0.label} — {priceLabel(m_0.priceIn)} / {priceLabel(m_0.priceOut)} за 1 млн токенов{m_0.vision ? " \xB7 \u0432\u0438\u0434\u0438\u0442 \u0444\u043E\u0442\u043E" : ""}</option>;
}
function _temp6(a_1) {
  return {
    id: a_1.id,
    title: a_1.shopName || "\u0411\u0435\u0437 \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u044F",
    sub: [a_1.contactName, a_1.phone, a_1.city].filter(Boolean).join(" \xB7 ")
  };
}
function _temp5(s_4) {
  return {
    id: s_4.id,
    title: s_4.name,
    sub: s_4.sub
  };
}
function _temp4() {}
function _temp3(sf_0) {
  return {
    id: sf_0.id,
    name: sf_0.name,
    sub: sf_0.location
  };
}
function _temp2(s_0) {
  return {
    id: s_0.shopId ?? s_0.id,
    name: s_0.shopName,
    sub: s_0.sellerName
  };
}
function _temp(s) {
  return s.shopId ?? s.id;
}
function Field(t0) {
  const $ = _c(9);
  const {
    label,
    htmlFor,
    hint,
    children
  } = t0;
  let t1;
  if ($[0] !== htmlFor || $[1] !== label) {
    t1 = <label htmlFor={htmlFor} className="text-sm font-medium">{label}</label>;
    $[0] = htmlFor;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== hint) {
    t2 = hint && <p className="text-xs text-muted-foreground">{hint}</p>;
    $[3] = hint;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children || $[6] !== t1 || $[7] !== t2) {
    t3 = <div className="min-w-0 space-y-1.5">{t1}{children}{t2}</div>;
    $[5] = children;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function Hint(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span className="block text-xs text-muted-foreground">{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function Check2(t0) {
  const $ = _c(14);
  const {
    checked,
    disabled,
    onChange,
    children
  } = t0;
  const t1 = disabled && "opacity-60";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex items-start gap-2 text-sm", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onChange) {
    t3 = e => onChange(e.target.checked);
    $[2] = onChange;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== checked || $[5] !== disabled || $[6] !== t3) {
    t4 = <input type="checkbox" className="mt-0.5 size-4" checked={checked} disabled={disabled} onChange={t3} />;
    $[4] = checked;
    $[5] = disabled;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== children) {
    t5 = <span>{children}</span>;
    $[8] = children;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t4 || $[12] !== t5) {
    t6 = <label className={t2}>{t4}{t5}</label>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}

/** Короткий список-выбор (магазины, заявки): одна строка — один вариант. */
function PickList(t0) {
  const $ = _c(11);
  const {
    items,
    selected,
    onSelect,
    empty
  } = t0;
  if (items.length === 0) {
    let t1;
    if ($[0] !== empty) {
      t1 = <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">{empty}</p>;
      $[0] = empty;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== items || $[3] !== onSelect || $[4] !== selected) {
    let t2;
    if ($[6] !== onSelect || $[7] !== selected) {
      t2 = item => {
        const active = item.id === selected;
        return <li key={item.id}><button type="button" role="option" aria-selected={active} onClick={() => onSelect(item.id)} className={cn("flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/60", active && "bg-primary/5")}><span className="min-w-0 flex-1"><span className="block truncate font-medium">{item.title}</span>{item.sub && <span className="block truncate text-xs text-muted-foreground">{item.sub}</span>}</span>{active && <Check className="size-4 shrink-0 text-primary" />}</button></li>;
      };
      $[6] = onSelect;
      $[7] = selected;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = items.map(t2);
    $[2] = items;
    $[3] = onSelect;
    $[4] = selected;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[9] !== t1) {
    t2 = <ul className="divide-y rounded-md border" role="listbox">{t1}</ul>;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}